import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import AlertComponent from "../_components/AlertComponent";
import LoadingSpinnerComponent from "../_components/LoadingSpinnerComponent";
import { billingActions } from "../_actions";
import MarkdownIt from "markdown-it";
import { getBool, toGermanDateString } from "../helper/formatting-helper";
import { determineCurrentLocale } from "../_helpers/LocaleHelper";
import { useLocation, useParams } from "react-router";
import { prepareConfiguration, prepareTemplateInvoice } from "../_helpers/mocking-helper";
import stringUtils from "../_helpers/string-utils";
import {
    AdditionalInformationLines,
    AddressBox,
    BasicInvoiceInformationBox,
    DiscountCodes,
    Footer,
    GiftCardLines,
    InvoiceLineItems,
    InvoiceNumber,
    InvoiceTotalBox,
    Logo,
    OpenFiskalLogo,
} from "./InvoiceComponents";
import useColors from "../helper/useColors";

const markdownIt = new MarkdownIt(/* Markdown-it options */);

const InvoiceTemplate = () => {
    const dispatch = useDispatch();
    const billing = useSelector(state => state.billing);
    const location = useLocation();
    const { id } = useParams();

    const params = useMemo(() => {
        return new URLSearchParams(location.search);
    }, [location.search]);

    const configurationId = useMemo(() => {
        return params.has("configurationId") ? params.get("configurationId") : null;
    }, [params]);

    const invoiceDate = useMemo(() => {
        return params.has("invoiceDate") ? params.get("invoiceDate") : toGermanDateString(new Date());
    }, [params]);

    const invoiceNumberParam = useMemo(() => {
        return params.has("invoiceNumber") ? params.get("invoiceNumber") : null;
    }, [params]);

    const languageParam = useMemo(() => {
        return params.has("language") ? params.get("language") : null;
    }, [params]);

    const isDummy = useMemo(() => {
        return params.has("dummy") ? getBool(params.get("dummy")) : false;
    }, [params]);

    const isReversalDocument = useMemo(() => {
        return params.has("reversal") ? getBool(params.get("reversal")) : false;
    }, [params]);

    useEffect(() => {
        if (configurationId) {
            dispatch(
                billingActions.getDocumentSettings(
                    configurationId,
                    id,
                    "INVOICE",
                    undefined,
                ),
            );
        }
    }, [dispatch, id, configurationId]);

    const invoiceData = useMemo(() => {
        if (billing.orderInvoicingConfiguration && billing.orderInvoicingConfiguration.externalUUID) {
            let ft = params.has("footerText") ? params.get("footerText") : billing.orderInvoicingConfiguration.footerText;
            let result = ft ? markdownIt.render(ft || "") : null;

            return {
                invoiceNumber: invoiceNumberParam || ((billing.orderInvoicingConfiguration.invoiceNumberPrefix || "") +
                    billing.orderInvoicingConfiguration.nextInvoiceNumber +
                    (billing.orderInvoicingConfiguration.invoiceNumberSuffix || "")),
                invoicingConfiguration: {
                    ...billing.orderInvoicingConfiguration,
                    logoAlignment: params.has("logoAlignment")
                        ? params.get("logoAlignment")
                        : billing.orderInvoicingConfiguration.logoAlignment,
                    secondaryColor: params.has("secondaryColor")
                        ? params.get("secondaryColor")
                        : billing.orderInvoicingConfiguration.secondaryColor,
                    primaryColor: params.has("primaryColor")
                        ? params.get("primaryColor")
                        : billing.orderInvoicingConfiguration.primaryColor,
                    footerText: result,
                    showLogo: params.has("showLogo") ? getBool(params.get("showLogo")) : billing.orderInvoicingConfiguration.showLogo,
                    uploadedLogoFile: params.has("logoUUID")
                        ? params.get("logoUUID")
                        : billing.orderInvoicingConfiguration.uploadedLogoFile,
                    testMode: billing.orderInvoicingConfiguration.testMode,
                },
                invoice: billing.invoice
                    ? billing.invoice
                    : isDummy
                        ? prepareTemplateInvoice(billing.orderInvoicingConfiguration)
                        : null,
            };
        } else {
            return {
                invoiceNumber: "I-1234",
                invoice: isDummy ? prepareTemplateInvoice(null) : null,
                invoicingConfiguration: {
                    ...prepareConfiguration(params),
                    testMode: params.has("testMode") ? getBool(params.get("testMode")) : true,
                },
            };
        }
    }, [billing.orderInvoicingConfiguration, billing.invoice, invoiceNumberParam, isDummy, params]);

    useEffect(() => {
        if (id) {
            dispatch(billingActions.getInvoiceData(id));
        }
    }, [dispatch, id]);

    let currentLocale = useMemo(() => {
        if (stringUtils.isBlank(languageParam) && billing.orderInvoicingConfiguration) {
            return determineCurrentLocale(billing.orderInvoicingConfiguration.language);
        } else {
            return determineCurrentLocale(languageParam);
        }
    }, [languageParam, billing.orderInvoicingConfiguration]);

    let hasDiscount = useMemo(() => {
        return invoiceData.invoice &&
            invoiceData.invoice.lineItems &&
            invoiceData.invoice.lineItems.filter((item) => item.discounts && item.discounts.length > 0).length > 0;
    }, [invoiceData.invoice]);

    const { primaryColor, secondaryColor } = useColors(invoiceData.invoicingConfiguration);

    const isMomoxCustomer = useMemo(() => {
        return invoiceData.invoicingConfiguration?.momoxCustomer;
    }, [invoiceData]);

    if (billing.loading || billing.invoicingConfigurationLoading) {
        return (
            <div className="container">
                <Row>
                    <Col>
                        <br />
                        <br />
                        <br />
                        <br />
                        <br />
                        <br />
                        <br />
                        <LoadingSpinnerComponent loading={true} size="6em" center={true} />
                    </Col>
                </Row>
            </div>
        );
    } else if (!invoiceData.invoice && !billing.errorMessage) {
        return (
            <div className="container">
                <br />
                <br />
                <br />
                <Row>
                    <Col>
                        <AlertComponent errorMessages={["Es gab ein Problem beim Laden der Rechnung"]} />
                    </Col>
                </Row>
                <br />
                <br />
                <br />
            </div>
        );
    } else if (billing.errorMessage) {
        return (
            <div className="container">
                <br />
                <br />
                <br />
                <Row>
                    <Col>
                        <AlertComponent errorMessages={billing.errorMessage} />
                    </Col>
                </Row>
                <br />
                <br />
                <br />
            </div>
        );
    } else if (!invoiceData.invoicingConfiguration) {
        return (
            <div className="container">
                <br />
                <br />
                <br />
                <Row>
                    <Col>
                        <AlertComponent errorMessages={["Es gab ein Problem beim Laden der Einstellungen"]} />
                    </Col>
                </Row>
                <br />
                <br />
                <br />
            </div>
        );
    } else {
        return (
            <div className="document-container">
                <div id="main-box">
                    <Logo logo={invoiceData.invoicingConfiguration.uploadedLogoFile}
                          logoAlignment={invoiceData.invoicingConfiguration.logoAlignment.toLowerCase()}
                          hidden={!invoiceData.invoicingConfiguration.showLogo} />

                    <InvoiceNumber
                        invoiceNumber={invoiceData.invoiceNumber}
                        isDummy={isDummy}
                        testMode={invoiceData.invoicingConfiguration.testMode}
                        isReversalDocument={isReversalDocument}
                        color={primaryColor}
                        locale={currentLocale} />

                    <br />

                    <AddressBox document={invoiceData.invoice}
                                displayEmailAddresses={invoiceData.invoicingConfiguration.displayEmailAddresses}
                                leftAlignedAddress={invoiceData.invoicingConfiguration.leftAlignedAddress}
                                color={primaryColor}
                                locale={currentLocale} />

                    <hr />

                    <BasicInvoiceInformationBox invoice={invoiceData.invoice}
                                                showDeliveryDate={invoiceData.invoicingConfiguration.showDeliveryDate}
                                                invoiceDate={invoiceDate}
                                                locale={currentLocale} />

                    <hr />
                    <br />

                    <InvoiceLineItems invoice={invoiceData.invoice}
                                      additionalInformationLines={invoiceData.invoicingConfiguration.additionalInformationLines}
                                      isReversalDocument={isReversalDocument}
                                      hasDiscount={hasDiscount}
                                      color={secondaryColor}
                                      locale={currentLocale} />

                    <InvoiceTotalBox
                        invoice={invoiceData.invoice}
                        hasDiscount={hasDiscount}
                        color={secondaryColor}
                        locale={currentLocale}
                        isMomoxCustomer={isMomoxCustomer}
                    />

                    {invoiceData.invoicingConfiguration.showGiftCardDetails && invoiceData.invoice.giftCardLines && invoiceData.invoice.giftCardLines.length > 0 && !isReversalDocument && (
                        <GiftCardLines giftCardLines={invoiceData.invoice.giftCardLines}
                                       locale={currentLocale}
                                       currencyCode={invoiceData.invoice.currencyCode} />
                    )}

                    {invoiceData.invoicingConfiguration.showDiscountDetails && invoiceData.invoice.discountLines && invoiceData.invoice.discountLines.length > 0 && !isReversalDocument && (
                        <DiscountCodes discountLines={invoiceData.invoice.discountLines} locale={currentLocale} />
                    )}

                    {invoiceData.invoicingConfiguration.additionalInformationLines &&
                        invoiceData.invoicingConfiguration.additionalInformationLines.filter(line => stringUtils.isBlank(line.linkedLineItemId)).length > 0 && (
                            <AdditionalInformationLines
                                additionalInformationLines={invoiceData.invoicingConfiguration.additionalInformationLines} />
                        )}

                    {invoiceData.invoicingConfiguration.ofiKey &&
                        <>
                            <br />
                            <OpenFiskalLogo shopId={invoiceData.invoice.shopId}
                                            orderId={invoiceData.invoice.orderId}
                                            ofiKey={invoiceData.invoicingConfiguration.ofiKey} />
                        </>
                    }
                </div>

                {invoiceData.invoicingConfiguration.footerText && invoiceData.invoicingConfiguration.footerText !== "" && (
                    <div id="footer-box">
                        <Footer text={invoiceData.invoicingConfiguration.footerText} />
                    </div>
                )}
            </div>
        );
    }
};

export default InvoiceTemplate;
